/**
 * Invoice Tab — 3 invoice variants (Full / Service Package / Spare Parts &
 * Labour). Each variant has its own preview, PDF download, WhatsApp send,
 * and email send. The preview uses `srcDoc` populated via authed axios so
 * the iframe doesn't trip the "Not authenticated" gate (iframes can't ride
 * the Authorization header automatically).
 */
import React, { useEffect, useState } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { toast } from "sonner";
import { FileText, Download, Send, RefreshCw, Eye, Package as PackageIcon, Wrench,
  Layers, MessageCircle, Mail, FileClock } from "lucide-react";
import { serviceOpsApi } from "../../../services/api";
import { formatDateTime } from "../../../utils/datetime";

const CATEGORIES = [
  {
    id: "full", label: "Full Invoice", icon: Layers,
    blurb: "Service package + spare parts + labour. Use this as the primary tax invoice.",
  },
  {
    id: "package", label: "Service Package Only", icon: PackageIcon,
    blurb: "Just the package line. Use when spares are billed separately or are pending.",
  },
  {
    id: "spares", label: "Spare Parts & Labour Only", icon: Wrench,
    blurb: "Only parts + labour rows. Use for add-on billing or in-warranty package jobs.",
  },
];

export default function InvoiceTab({ lead, onChanged }) {
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [category, setCategory] = useState("full");
  const [proforma, setProforma] = useState(false);
  const [channels, setChannels] = useState({ whatsapp: true, email: true });
  const [extraEmail, setExtraEmail] = useState("");

  // Preview HTML loaded via authed axios → set as iframe srcDoc.
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");

  const invoiceNumber = lead?.invoice_number;
  const hasInvoice = !!lead?.invoice_id;

  const loadPreview = async (cat = category, pro = proforma) => {
    if (!lead?.id) return;
    setPreviewLoading(true);
    setPreviewError("");
    try {
      const { data } = await serviceOpsApi.invoicePreviewHtml(lead.id, cat, pro);
      setPreviewHtml(data);
    } catch (e) {
      setPreviewError(e?.response?.data?.detail || e?.message || "Preview failed");
      setPreviewHtml("");
    } finally {
      setPreviewLoading(false);
    }
  };

  // Refresh preview on lead/category/proforma change.
  useEffect(() => {
    loadPreview(category, proforma);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.id, lead?.spare_parts_total, lead?.final_price, category, proforma]);

  const onGenerate = async () => {
    setGenerating(true);
    try {
      const res = await serviceOpsApi.generateInvoice(lead.id);
      toast.success(`Invoice ${res?.data?.invoice_number} generated`);
      onChanged?.();
      loadPreview(category);
    } catch (e) {
      toast.error("Generate failed: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setGenerating(false);
    }
  };

  const onDownloadPdf = async () => {
    try {
      const { data } = await serviceOpsApi.invoicePdfBlob(lead.id, category, proforma);
      const blobUrl = URL.createObjectURL(data);
      const w = window.open(blobUrl, "_blank", "noopener");
      if (!w) {
        const a = document.createElement("a");
        a.href = blobUrl;
        const num = invoiceNumber || "invoice";
        const parts = [];
        if (proforma) parts.push("proforma");
        if (category !== "full") parts.push(category);
        const suffix = parts.length ? `-${parts.join("-")}` : "";
        a.download = `${num}${suffix}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (e) {
      toast.error("Download failed: " + (e?.response?.data?.detail || e?.message));
    }
  };

  const onSend = async () => {
    setSending(true);
    try {
      const chans = Object.keys(channels).filter((c) => channels[c]);
      if (chans.length === 0) {
        toast.error("Pick at least one channel (WhatsApp / Email)");
        setSending(false);
        return;
      }
      const res = await serviceOpsApi.sendInvoice(lead.id, {
        channels: chans,
        extra_emails: extraEmail ? [extraEmail] : [],
        category,
        proforma,
      });
      const waOk = res?.data?.whatsapp?.success;
      const emOk = res?.data?.email?.success;
      const msg = [];
      if (channels.whatsapp) msg.push(`WhatsApp ${waOk ? "✓" : "✗"}`);
      if (channels.email) msg.push(`Email ${emOk ? "✓" : "✗"}`);
      const docLabel = proforma ? "Proforma" : "Invoice";
      toast.success(`${docLabel} sent (${CATEGORIES.find((c) => c.id === category).label}): ${msg.join(" · ")}`);
      onChanged?.();
    } catch (e) {
      toast.error("Send failed: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setSending(false);
    }
  };

  const currentCat = CATEGORIES.find((c) => c.id === category);

  return (
    <div className="p-6 space-y-4" data-testid="invoice-tab">
      {/* ───── Status card ───── */}
      <div className="border rounded-lg p-4 bg-slate-50" data-testid="invoice-status">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">Invoice</div>
            {hasInvoice ? (
              <div>
                <div className="text-lg font-bold text-slate-900 font-mono">{invoiceNumber}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Generated {lead?.invoice_generated_at ? formatDateTime(lead.invoice_generated_at) : "—"}
                  {lead?.invoice_sent_at && <> · Last sent {formatDateTime(lead.invoice_sent_at)}</>}
                </div>
              </div>
            ) : (
              <div className="text-sm text-slate-500 italic mt-1">
                No invoice generated yet. Click "Generate Invoice" to mint the canonical
                invoice number — preview &amp; PDF for all 3 variants will then be available.
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={onGenerate} disabled={generating}
              variant={hasInvoice ? "outline" : "default"}
              data-testid="invoice-generate-btn">
              <FileText className="h-4 w-4 mr-1.5" />
              {generating ? "Generating..." : hasInvoice ? "Regenerate" : "Generate Invoice"}
            </Button>
          </div>
        </div>
      </div>

      {/* ───── Doc-type toggle (Tax invoice vs Proforma) ───── */}
      <div className="flex items-center gap-3 border rounded-lg p-3 bg-amber-50/30 border-amber-200" data-testid="invoice-doctype-toggle">
        <FileClock className="h-4 w-4 text-amber-700 shrink-0" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-slate-800">Document type</div>
          <div className="text-xs text-slate-600">
            Switch to <b>Proforma</b> when sharing pricing with the customer <i>before payment</i> —
            it's clearly marked "PROFORMA" and skips the "Balance Due" line.
          </div>
        </div>
        <div className="flex border rounded-md overflow-hidden text-sm bg-white" data-testid="invoice-doctype-pills">
          <button type="button"
            onClick={() => setProforma(false)}
            className={`px-3 py-1.5 font-medium transition ${!proforma ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}
            data-testid="doctype-tax-btn">
            Tax Invoice
          </button>
          <button type="button"
            onClick={() => setProforma(true)}
            className={`px-3 py-1.5 font-medium transition ${proforma ? "bg-amber-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}
            data-testid="doctype-proforma-btn">
            Proforma
          </button>
        </div>
      </div>

      {/* ───── Category selector ───── */}
      <div className="grid grid-cols-3 gap-3" data-testid="invoice-category-tabs">
        {CATEGORIES.map((c) => {
          const Icon = c.icon;
          const active = category === c.id;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => setCategory(c.id)}
              className={`text-left rounded-lg border-2 p-3 transition ${
                active
                  ? "border-blue-500 bg-blue-50 shadow-sm"
                  : "border-slate-200 hover:border-blue-300 hover:bg-slate-50"
              }`}
              data-testid={`invoice-cat-${c.id}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon className={`h-4 w-4 ${active ? "text-blue-700" : "text-slate-500"}`} />
                <div className={`font-semibold text-sm ${active ? "text-blue-900" : "text-slate-700"}`}>{c.label}</div>
              </div>
              <div className="text-xs text-slate-500 leading-tight">{c.blurb}</div>
            </button>
          );
        })}
      </div>

      {/* ───── Preview ───── */}
      <div className="border rounded-lg overflow-hidden bg-white" data-testid="invoice-preview-frame">
        <div className="bg-slate-50 px-4 py-2 border-b flex items-center justify-between">
          <span className="text-sm font-semibold flex items-center gap-2">
            <Eye className="h-4 w-4" /> Preview · {proforma ? "Proforma · " : ""}{currentCat.label}
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline"
              onClick={() => loadPreview(category)}
              disabled={previewLoading}
              data-testid="invoice-preview-refresh-btn">
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${previewLoading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button size="sm" variant="outline"
              onClick={onDownloadPdf}
              data-testid="invoice-download-pdf">
              <Download className="h-3.5 w-3.5 mr-1" />
              Download PDF
            </Button>
          </div>
        </div>
        {previewError ? (
          <div className="p-6 text-sm text-red-600" data-testid="invoice-preview-error">
            Preview failed: {previewError}
          </div>
        ) : (
          <iframe
            title={`invoice-preview-${category}`}
            srcDoc={previewHtml}
            className="w-full h-[480px] bg-white"
            data-testid="invoice-iframe"
          />
        )}
      </div>

      {/* ───── Send block ───── */}
      <div className={`border rounded-lg p-4 ${(hasInvoice || proforma) ? "bg-emerald-50/40 border-emerald-200" : "bg-slate-50 opacity-60"}`} data-testid="invoice-send">
        <div className="text-sm font-semibold text-emerald-900 mb-3 flex items-center gap-2">
          <Send className="h-4 w-4" /> Send {proforma ? "Proforma" : "Invoice"} · {currentCat.label}
        </div>
        <div className="grid grid-cols-12 gap-3 items-end">
          <div className="col-span-3 space-y-2">
            <Label className="text-xs">Channels</Label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={channels.whatsapp}
                onChange={(e) => setChannels({ ...channels, whatsapp: e.target.checked })}
                data-testid="invoice-channel-whatsapp"
              />
              <MessageCircle className="h-3.5 w-3.5 text-emerald-600" />
              WhatsApp ({lead?.mobile || "—"})
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={channels.email}
                onChange={(e) => setChannels({ ...channels, email: e.target.checked })}
                data-testid="invoice-channel-email"
              />
              <Mail className="h-3.5 w-3.5 text-blue-600" />
              Email ({lead?.email || "—"})
            </label>
          </div>
          <div className="col-span-5">
            <Label className="text-xs">CC additional email (optional)</Label>
            <Input
              value={extraEmail}
              onChange={(e) => setExtraEmail(e.target.value)}
              placeholder="finance@yourcompany.com"
              data-testid="invoice-extra-email"
            />
          </div>
          <div className="col-span-4">
            <Button onClick={onSend} disabled={(!hasInvoice && !proforma) || sending} className="w-full" data-testid="invoice-send-btn">
              <Send className="h-4 w-4 mr-1.5" />
              {sending ? "Sending..." : `Send ${proforma ? "Proforma" : currentCat.label}`}
            </Button>
          </div>
        </div>
        {!hasInvoice && !proforma && (
          <div className="text-xs text-slate-500 italic mt-2">
            Generate the invoice first to enable sending — or switch to <b>Proforma</b> above to send pre-payment.
          </div>
        )}
      </div>
    </div>
  );
}
